import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  CardActions,
  Tooltip,
  Select,
  MenuItem,
  Pagination,
  Stack,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import { AssessmentOutlined, GraphicEqRounded, Man4Outlined, ManOutlined, MapOutlined, NordicWalkingOutlined, PrintOutlined, ReportOutlined, WidthFull } from "@mui/icons-material";
import { Toast } from "primereact/toast";
import CameraTimeout from "./CameraTimeout";
import dayjs from "dayjs";
import { flespiEventHandlerMessage, getApiErrorMessage } from "../../../../functions/functions";
import getReportWithFiltersResponse from "../../../../resources/Api/report/getReportWithFiltersResponse";
import { ReportType, SelectedParametersType, UserType } from "../../../../types/types";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../../../atoms/atoms";
import { useCookies } from "react-cookie";
import { getReportDateRangeMillis } from "../../../../utils/reportDateRange";

const ITEMS_PER_PAGE = 6;

interface FlespiMedia {
  url: string;
  created: number;
  name?: string;
  meta?: {
    type: "video" | "image";
  };
}

interface FlespiMessage {
  timestamp: number;
  "dsm.event.name"?: string;
  "adas.event.name"?: string;
  "driver.event.name"?: string;
  "event.enum"?: number;
  "position.latitude"?: number;
  "position.longitude"?: number;
  media?: boolean;
  [key: string]: any; // Para as chaves dinâmicas media.video.1, etc.
}

interface TimelineItem {
  url: string;
  created: number;
  type: "video" | "image";
  latitude: number;
  longitude: number;
  eventType: string;
  filename: string;
}

const toNumber = (v: any) => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const formatDate = (unix: any) =>
  unix ? new Date(toNumber(unix) * 1000).toLocaleString() : "-";

const FlespiVideoTimelineList = ({
  data,
  label,
  status,
  onClose,
  startDate,
  endDate,
  selectedVehicleId
}: any) => {
  // atoms
  const getCurrentUser = useRecoilValue<UserType>(userAtom);

  const toastRef = useRef<any>(null);
  const [page, setPage] = useState(1);
  const [mediaFilter, setMediaFilter] = useState("all");
  const [mediaEventFilter, setMediaEventFilter] = useState("all");

  const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
  const [exportType, setExportType] = useState<string>("");

  // cookies
  const [cookies, _] = useCookies(["selectedParameters", "token"]);

  // const [startDate, setStartDate] = useState<any>(dayjs().startOf("day"));
  // const [endDate, setEndDate] = useState<any>(dayjs().endOf("day"));

  const downloadFromUrl = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Erro ao baixar arquivo",
        life: 3000,
      });
    }
  };

  const handlePrint = () => {
    const images = paginated.filter((item: any) => item.type === "image");
    if (images.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Nenhuma imagem nesta página para imprimir",
        life: 3000,
      });
      return;
    }
    const htmlImages = images
      .map(
        (img: any) =>
          `<div class="card"><img src="${img.url}"/><div class="info"><div><b>Data:</b> ${formatDate(img.created)}</div><div><b>Evento:</b> ${flespiEventHandlerMessage(img.eventType)}</div></div></div>`,
      )
      .join("");
    const win = window.open("", "", "width=1200,height=800");
    if (!win) {
      toastRef.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível abrir a janela de impressão",
        life: 3000,
      });
      return;
    }
    win.document.write(`<html><head><title>Eventos câmera</title><style>
body{font-family:Arial;padding:20px;background:#fff}
h2{margin-bottom:20px}
.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
.card{border:1px solid #ddd;border-radius:10px;padding:10px}
img{width:100%;border-radius:8px}
.info{font-size:12px;margin-top:6px}
</style></head><body>
<h2>Relatório de Eventos - ${label} - ${dayjs(startDate).format("DD/MM/YYYY HH:mm:ss")} até ${dayjs(endDate).format("DD/MM/YYYY HH:mm:ss")}</h2>
<div class="grid">${htmlImages}</div>
</body></html>`);
    win.document.close();
    setTimeout(() => {
      win.print();
    }, 500);
  };

  const onHandleFullScreen = (url: string, name: string) => {
    if (!document.fullscreenElement) {
      const img = document.createElement("img");
      img.src = url;
      img.id = name;
      img.style.objectFit = "contain";
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.background = "black";
      document.body.appendChild(img);
      img.requestFullscreen();
      img.addEventListener("fullscreenchange", () => {
        if (!document.fullscreenElement) {
          document.body.removeChild(img);
        }
      });
    } else {
      document.exitFullscreen();
    }
  };

  const parsedData = useMemo(() => {
    if (!data) return null;
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }
    return data;
  }, [data]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const messages = (parsedData?.result as FlespiMessage[]) ?? [];
    const items: TimelineItem[] = [];

    // Agrupador por segundo (timestamp inteiro)
    const groupedByTime: Record<number, {
      lat: number;
      lon: number;
      eventType: string;
      mediaFiles: any[]
    }> = {};

    messages.forEach((msg) => {
      const timeKey = Math.floor(msg.timestamp);

      if (!groupedByTime[timeKey]) {
        groupedByTime[timeKey] = {
          lat: msg["position.latitude"] ?? 0,
          lon: msg["position.longitude"] ?? 0,
          eventType: "unknown",
          mediaFiles: []
        };
      }

      // Identifica o evento (Prioridade para DSM, depois ADAS)
      const eventName = msg["dsm.event.name"] || msg["adas.event.name"] || msg["driver.event.name"];
      if (eventName) {
        groupedByTime[timeKey].eventType = eventName;
      }

      // Coleta as mídias dentro da mensagem
      Object.keys(msg).forEach((key) => {
        if (key.startsWith("media.image") || key.startsWith("media.video")) {
          const media = msg[key] as FlespiMedia;
          if (media?.url) {
            groupedByTime[timeKey].mediaFiles.push({
              url: media.url,
              created: media.created,
              type: key.includes("video") ? "video" : "image",
              filename: media.name || `file-${media.created}`
            });
          }
        }
      });
    });

    // Transforma o mapa em lista plana
    Object.values(groupedByTime).forEach((group) => {
      group.mediaFiles.forEach((file) => {
        items.push({
          url: file.url,
          created: file.created,
          type: file.type,
          latitude: group.lat,
          longitude: group.lon,
          eventType: group.eventType,
          filename: file.filename,
        });
      });
    });

    return items.sort((a, b) => b.created - a.created);
  }, [parsedData]);

  const filteredTimeline = useMemo(() => {
    const start = startDate?.unix();
    const end = endDate?.unix();
    setPage(1);
    return timeline.filter((item: any) => {
      if (mediaFilter !== "all" && item.type !== mediaFilter) return false;
      if (mediaEventFilter !== "all" && item.eventType !== mediaEventFilter) return false;
      if (start && item.created < start) return false;
      if (end && item.created > end) return false;
      return true;
    });
  }, [timeline, mediaFilter, mediaEventFilter, startDate, endDate]);

  const totalPages = Math.ceil(filteredTimeline.length / ITEMS_PER_PAGE);

  const paginated = filteredTimeline.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const onOpenStreetViewer = useCallback(async (lat: number, lng: number) => {
    if (!lat || !lng) return;
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat}%2C${lng}&heading=${0}`;
    window.open(url, "_blank");
  }, []);

  useEffect(() => {
    if (report.byteLength > 1) {
      const typeExport =
        exportType === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const file = new Blob([report], { type: typeExport });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
      setReport(new ArrayBuffer(0));
    }
  }, [report, exportType]);

  const getReport = useCallback(
    async (type: string) => {
      // setLoading(true);
      let dataBuffer = new ArrayBuffer(0);
      const currentParameters: SelectedParametersType = cookies.selectedParameters;
      const customerId = currentParameters?.customer.id;
      const departmentId = currentParameters?.department.id;
      const reportDateRange = getReportDateRangeMillis({
        startDate,
        endDate,
        isManager: getCurrentUser.configs.isManager,
        authenticatedCustomerUtcZone:
          getCurrentUser.configs.customerSettings?.utcZone,
      });

      const filters: ReportType = {
        name: "Diário de bordo",
        reportId: 9,
        ...reportDateRange,
        vehicles: [selectedVehicleId],
        groups: [],
        drivers: [],
        customerId: customerId,
        departmentId: departmentId,
      };

      let reportType = type;
      let isSimpleType = false;

      // simpleXlsxExport = false
      const status = "activated";
      try {

        const { data } = await getReportWithFiltersResponse(
          cookies.token,
          filters,
          status,
          reportType,
          currentParameters?.isViewingAll,
          isSimpleType
        );

        if ((data as ArrayBuffer).byteLength === 0) {
          toastRef.current?.show({
            severity: "warn",
            summary: "Aviso",
            detail: "Nenhum dado para mostrar no relatório.",
            life: 3000,
          });
        } else {
          toastRef.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Relatório gerado com sucesso.",
            life: 3000,
          });
          dataBuffer = data;
        }
        setTimeout(() => {
          setReport(dataBuffer);
          setExportType(type);
        }, 3000);
      } catch (error: any) {
        const reason = getApiErrorMessage(error, "Erro ao gerar o relatório. Tente novamente.");

        // setLoading(false);
        toastRef.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail: reason,
          life: 3000,
        });
      }
    },
    [
      selectedVehicleId,
      cookies.token,
      cookies.selectedParameters,
      startDate,
      endDate,
      getCurrentUser.configs.isManager,
      getCurrentUser.configs.customerSettings?.utcZone,
    ]
  );

  return (
    <>
      <Toast ref={toastRef} position="top-right" />
      <Box
        className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-gtr-surface tw-text-gtr-text"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-bg-gtr-sidebar tw-text-white"
          sx={{
            px: 2,
            py: 1.5,
            backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box className="tw-min-w-0">
            <Typography className="tw-truncate" variant="h6" sx={{ fontFamily: "Prometo", fontWeight: 700 }}>Linha do tempo de eventos</Typography>
            <Typography className="tw-block tw-truncate" variant="caption" sx={{ opacity: 0.82 }}>{label}</Typography>
          </Box>
          <Box className="tw-flex tw-flex-none tw-items-center tw-gap-1">
            <Tooltip title="Imprimir eventos" arrow>
              <IconButton aria-label="Imprimir eventos" onClick={() => handlePrint()} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", transform: "translateY(-1px)" } }}>
                <PrintOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir diário de bordo correlacionado" arrow>
              <IconButton aria-label="Imprimir diário de bordo correlacionado" onClick={() => getReport("pdf")} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", transform: "translateY(-1px)" } }}>
                <AssessmentOutlined />
              </IconButton>
            </Tooltip>
            {onClose && (
              <Tooltip title="Fechar" arrow>
                <IconButton aria-label="Fechar linha do tempo" onClick={onClose} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", transform: "translateY(-1px)" } }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider />

        <Box className="tw-grid tw-w-full tw-grid-cols-1 tw-gap-3 tw-bg-gtr-surface-muted tw-p-3 sm:tw-grid-cols-2" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, width: "100%", gap: 1.5, padding: 1.5 }}>
          <Box
            className="tw-w-full tw-rounded-xl tw-border tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3"
            sx={{
              p: 1.5,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Tipo de mídia
            </Typography>
            <FormControl size="small">
              <Select
                className="tw-bg-gtr-surface"
                sx={{ width: "100%", height: 52, borderRadius: 2, "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,108,119,0.10)" } }}
                value={mediaFilter}
                onChange={(e) => setMediaFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="image">Imagem</MenuItem>
                <MenuItem value="video">Vídeo</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            className="tw-w-full tw-rounded-xl tw-border tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3"
            sx={{
              p: 1.5,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Tipo de evento
            </Typography>
            <FormControl size="small">
              <Select
                className="tw-bg-gtr-surface"
                sx={{ width: "100%", height: 52, borderRadius: 2, "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,108,119,0.10)" } }}
                value={mediaEventFilter}
                onChange={(e) => setMediaEventFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="Answer and call the police">Uso de telefone</MenuItem>
                <MenuItem value="Fatique driving alarm">Fadiga</MenuItem>
                <MenuItem value="Distracted driving alarm">Desvio de atenção</MenuItem>
                <MenuItem value="Smoking alarm">Fumando na condução</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box
          className="tw-grid tw-grid-cols-1 tw-gap-3 tw-bg-gtr-surface-muted tw-p-3 md:tw-grid-cols-2"
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 1.5,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          {status === "streaming" && filteredTimeline.length === 0 && (
            <Box
              className="tw-bg-gtr-surface-muted tw-text-gtr-text-muted"
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                borderRadius: 2,
                boxShadow: 0,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Nenhum arquivo encontrado
                <br />
                para o período selecionado
              </Typography>
            </Box>
          )}

          {status === "streaming" &&
            paginated.map((item: any, index: number) => (
              <Box
                className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3 tw-shadow-gtr-card tw-transition-all tw-duration-200 hover:tw-translate-y-[-1px]"
                key={index}
                sx={{
                  borderRadius: 3,
                  p: 1.5,
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <Tooltip title="Ver Local">
                  <IconButton
                    size="small"
                    sx={{
                      background: "#008f18",
                      color: "#ffffff",
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 10,
                      width: 30,
                      height: 30,
                      "&:hover": { background: "#007a14" },
                    }}
                    onClick={() => onOpenStreetViewer(item?.latitude, item?.longitude)}
                  >
                    <MapOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <Box>
                  <Typography variant="body2">
                    <b>Data:</b> {formatDate(item.created)}
                  </Typography>

                  {mediaEventFilter && <Typography variant="body2">
                    <b>Evento:</b> {flespiEventHandlerMessage(item.eventType)}
                  </Typography>}

                  <Typography variant="body2">
                    <b>Local:</b> {item.latitude?.toFixed(4)} / {item.longitude?.toFixed(4)}
                  </Typography>
                </Box>

                {item.type === "image" && (
                  <img
                    src={item.url}
                    onClick={() => onHandleFullScreen(item.url, item.filename)}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      marginTop: 8,
                      cursor: "zoom-in",
                    }}
                  />
                )}

                {item.type === "video" && (
                  <video
                    src={item.url}
                    controls
                    style={{ width: "100%", borderRadius: 8, marginTop: 8 }}
                  />
                )}

                <CardActions className="tw-mt-2 tw-justify-end tw-border-t tw-border-gtr-border/60 tw-px-0 tw-pb-0 tw-pt-2" sx={{ justifyContent: "flex-end", gap: 1, px: 0, pb: 0 }}>
                  <Tooltip title="Baixar arquivo" arrow>
                    <IconButton
                      aria-label="Baixar arquivo"
                      onClick={() => downloadFromUrl(item.url, item.filename)}
                      sx={{ color: "#00B700", backgroundColor: "rgba(0,183,0,0.08)", "&:hover": { backgroundColor: "rgba(0,183,0,0.16)", transform: "translateY(-1px)" } }}
                    >
                      <DownloadOutlined />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Box>
            ))}
        </Box>

        {totalPages > 1 && (
          <Box className="tw-flex tw-justify-center tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3" sx={{ display: "flex", justifyContent: "center", p: 1.5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
            />
          </Box>
        )}
        <Divider />
        <Box className="tw-bg-gtr-surface" sx={{ p: 2 }}>
          {status === "timeout" && <CameraTimeout onRetry={() => { }} />}
        </Box>
      </Box>
    </>
  );
};

export default FlespiVideoTimelineList;
